import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import toast from 'react-hot-toast'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DataBlock,
  InputGroup
} from '@/common'
import { UnitLabel } from '@/shared/config/config'
import useI18n from '@/shared/hooks/useI18n'
import MassBalanceCalculatorForm from './MassBalanceCalculatorForm'
import { Drawer } from '@/shared/components'
import { MassBalanceItem } from '@/api/EmissionReportModel'
import { RATIO_OF_MOLLAR_MASSES } from '@/features/reports/formulas/constants'

const initState = (initialValues?: MassBalanceItem | null) => {
  return {
    materialName: initialValues?.materialName || '',
    description: initialValues?.description || '',
    quantity: initialValues ? String(initialValues.quantity) : '',
    carbonContent: initialValues ? String(initialValues.carbonContent) : ''
  }
}
export default function GenericMassBalanceItemPanel({
  isOpen,
  close,
  add,
  title,
  initialValues
}: {
  isOpen: boolean
  close: () => void
  add: (item: MassBalanceItem) => void
  title: string
  initialValues?: MassBalanceItem | null
}) {
  const { t, numberFormatter3Decimals } = useI18n()
  const [state, setState] = useState(initState(initialValues))

  const schema = useMemo(
    () =>
      z
        .object({
          materialName: z.string().min(1),
          description: z.string().optional(),
          quantity: z.number().nullable(),
          carbonContent: z.number().gte(0),
          emission: z.number().gte(0)
        })
        .refine(({ quantity }) => quantity !== null, {
          message: t('common:validations.quantityRequiredZeroAllowed'),
          path: ['quantity']
        }),
    [t]
  )

  const emission = useMemo(() => {
    const quantity = Number(state.quantity || 0)
    const carbonContent = Number(state.carbonContent || 0)
    return quantity * carbonContent * RATIO_OF_MOLLAR_MASSES
  }, [state])

  const onQuantityChange = (quantity: string) => {
    setState({
      ...state,
      quantity
    })
  }

  const onCarbonContentChange = (carbonContent: string) => {
    setState({
      ...state,
      carbonContent
    })
  }

  const formatEmission = useMemo(() => {
    return `${numberFormatter3Decimals(emission)} ${UnitLabel.tCO2PerT}`
  }, [emission, numberFormatter3Decimals])

  const payload = useMemo(() => {
    return {
      materialName: state.materialName,
      quantity: state.quantity === '' ? null : Number(state.quantity),
      carbonContent: state.carbonContent === '' ? 0 : Number(state.carbonContent),
      description: state.description,
      emission
    }
  }, [state, emission])

  const validationResult = useMemo(() => {
    return schema.safeParse(payload)
  }, [payload, schema])

  const closeAndReset = useCallback(() => {
    setState(initState())
    close()
  }, [close])

  const onAdd = useCallback(() => {
    if (!validationResult.success) {
      toast.error(validationResult.error.toString())
      return
    }
    add({
      materialName: payload.materialName,
      description: payload.description,
      quantity: Number(state.quantity || 0),
      carbonContent: Number(state.carbonContent || 0),
      emission
    })
    closeAndReset()
  }, [add, validationResult, emission, payload, state, closeAndReset])

  useEffect(() => {
    if (initialValues) {
      setState(initState(initialValues))
    }
  }, [initialValues])

  return (
    <Drawer isOpen={isOpen} close={closeAndReset}>
      <Card className="min-w-[400px]">
        <CardHeader>
          <CardTitle title={t('report:massBalance.genericMassBalanceItemPanel.title')} />
        </CardHeader>
        <CardBody className="space-y-4 p-4">
          <div className="text-text-secondary">{title}</div>
          <div className="space-y-4">
            <InputGroup
              label={t('report:massBalance.genericMassBalanceItemPanel.fields.materialName.label')}
              value={state.materialName}
              onChange={evt => setState({ ...state, materialName: evt.target.value })}
            />
            <InputGroup
              label={t('report:massBalance.genericMassBalanceItemPanel.fields.description.label')}
              value={state.description}
              onChange={evt => setState({ ...state, description: evt.target.value })}
            />
            <MassBalanceCalculatorForm
              quantity={state.quantity}
              carbonContent={state.carbonContent}
              onQuantityChange={onQuantityChange}
              onCarbonContentChange={onCarbonContentChange}
            />
            <DataBlock label={t('report:common.emission')} data={formatEmission} />
          </div>
        </CardBody>
        <footer className="grid grid-cols-2 gap-4 p-4">
          <Button variant="subtle" onClick={closeAndReset}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="default" onClick={onAdd} disabled={!validationResult.success}>
            {t('common:actions.add')}
          </Button>
        </footer>
      </Card>
    </Drawer>
  )
}
